import React, { Component } from "react";
import CartContext from "../StoreManager/CartContext";
import { Table, Button, Modal, Form, Alert } from "react-bootstrap";

export default class HotelRoomType extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      items: [],
      showModal: false,
      error: null,
      newItem: {
        storeId: "", // will be set from context
        code: "",
        name: "",
        description: "",
        maxAdults: 2,
        maxChildren: 0,
      },
      editItem: null,
      loading: false,
    };
  }

  componentDidMount() {
    // load after mount
    this.loadData();
  }

  async loadData() {
    const storeId = this.context?.storeId;
    if (!storeId) {
      this.setState({ error: "StoreId not found in context." });
      return;
    }

    this.setState({ loading: true, error: null });
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API}HotelRoomType?storeId=${storeId}`
      );
      if (!res.ok) throw new Error(`API error ${res.status}`);
      const data = await res.json();
      this.setState({ items: data });
    } catch (err) {
      console.error("Error loading room types:", err);
      this.setState({ error: "Failed to load room types." });
    } finally {
      this.setState({ loading: false });
    }
  }

  handleShowModal = (item = null) => {
    const storeId = this.context?.storeId || "";
    this.setState({
      showModal: true,
      editItem: item,
      newItem: item || {
        storeId: storeId,
        code: "",
        name: "",
        description: "",
        maxAdults: 2,
        maxChildren: 0,
      },
    });
  };

  handleCloseModal = () =>
    this.setState({ showModal: false, editItem: null, error: null });

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      newItem: { ...prev.newItem, [name]: value },
    }));
  };

  async handleSave() {
    const { newItem, editItem } = this.state;
    const storeId = this.context?.storeId;
    if (!storeId) {
      this.setState({ error: "StoreId not found in context." });
      return;
    }

    // ensure storeId set from context (override any tampering)
    newItem.storeId = storeId;

    // basic validation
    if (!newItem.code || !newItem.name) {
      this.setState({ error: "Code and Name are required." });
      return;
    }

    const apiBase = `${process.env.REACT_APP_API}HotelRoomType`;
    // append storeId in query so the controller's TryGetStoreId finds it
    const url = editItem
      ? `${apiBase}/${editItem.roomTypeId}?storeId=${storeId}`
      : `${apiBase}?storeId=${storeId}`;

    const payload = {
      storeId: newItem.storeId,
      code: newItem.code,
      name: newItem.name,
      description: newItem.description,
      maxAdults: parseInt(newItem.maxAdults, 10) || 0,
      maxChildren: parseInt(newItem.maxChildren, 10) || 0,
    };

    try {
      const res = await fetch(url, {
        method: editItem ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        // try to read server response to show meaningful error
        let text;
        try {
          text = await res.text();
        } catch {
          text = `${res.statusText}`;
        }
        console.error("Save failed:", res.status, text);
        this.setState({ error: `Save failed: ${res.status} ${text}` });
        return;
      }

      this.handleCloseModal();
      await this.loadData();
    } catch (err) {
      console.error("Error saving room type:", err);
      this.setState({
        error: "Failed to save room type. See console for details.",
      });
    }
  }

  async handleDelete(id) {
    const storeId = this.context?.storeId;
    if (!storeId) {
      this.setState({ error: "StoreId not found in context." });
      return;
    }
    if (!window.confirm("Are you sure you want to delete this room type?"))
      return;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API}HotelRoomType/${id}?storeId=${storeId}`,
        { method: "DELETE" }
      );

      if (!res.ok) {
        const txt = await res.text().catch(() => res.statusText);
        console.error("Delete failed:", res.status, txt);
        this.setState({ error: `Delete failed: ${res.status} ${txt}` });
        return;
      }

      await this.loadData();
    } catch (err) {
      console.error("Error deleting room type:", err);
      this.setState({
        error: "Failed to delete room type. See console for details.",
      });
    }
  }

  render() {
    const { items, showModal, newItem, editItem, error, loading } = this.state;

    return (
      <div className="container mt-4">
        <h4>Hotel Room Types</h4>

        {error && <Alert variant="danger">{error}</Alert>}

        <Button
          variant="primary"
          className="mb-3"
          onClick={() => this.handleShowModal()}
        >
          Add Room Type
        </Button>

        {loading ? (
          <div>Loading...</div>
        ) : (
          <Table bordered hover responsive>
            <thead className="table-light">
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Description</th>
                <th>Adults</th>
                <th>Children</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((rt) => (
                <tr key={rt.roomTypeId}>
                  <td>{rt.code}</td>
                  <td>{rt.name}</td>
                  <td>{rt.description}</td>
                  <td>{rt.maxAdults}</td>
                  <td>{rt.maxChildren}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => this.handleShowModal(rt)}
                    >
                      Edit
                    </Button>{" "}
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => this.handleDelete(rt.roomTypeId)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        <Modal show={showModal} onHide={this.handleCloseModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              {editItem ? "Edit Room Type" : "Add Room Type"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Code</Form.Label>
                <Form.Control
                  name="code"
                  value={newItem.code}
                  onChange={this.handleChange}
                  placeholder="E.g., DLX"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  name="name"
                  value={newItem.name}
                  onChange={this.handleChange}
                  placeholder="E.g., Deluxe Room"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  name="description"
                  rows={2}
                  value={newItem.description}
                  onChange={this.handleChange}
                  placeholder="Optional room description"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Max Adults</Form.Label>
                <Form.Control
                  type="number"
                  name="maxAdults"
                  value={newItem.maxAdults}
                  onChange={this.handleChange}
                  min="1"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Max Children</Form.Label>
                <Form.Control
                  type="number"
                  name="maxChildren"
                  value={newItem.maxChildren}
                  onChange={this.handleChange}
                  min="0"
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => this.handleSave()}>
              {editItem ? "Update" : "Save"}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}
